import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateConnectBusinessState, resetConnectBusinessState } from "../../../../../../../../../redux/features/connectBusinessStateSlice";
import { Box, Typography, Button } from "@mui/material";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { useNavigate } from "react-router-dom";
import { BaseForm } from "../../../../../../../../../components/Form";
import { TFormValues } from "../../../../../../../../../components/Form/types/form.types";
import { useCreateCompany } from "./useCreateCompany";
import { ConnectBusinessAlgorithm } from "../../../../../../../../../components/connectBusinessAlgorithm";
import { colorsConfig } from "../../../../../../../../../config/colors.config";
import { Dialog } from "../../../../../../../../../components/Dialog";
import { useAppSelector } from "../../../../../../../../../redux/hooks/redux";
import { selectCurrentCompany, selectRole } from "../../../../../../../../../redux/features/user/selectors/userState.selectors";
import { getBasePath } from "../../../../../../../../../utils";
import { flowHubHeroCardsFormStyles } from "../config/flowHubHeroCardsForm.config";
import { companyFormFields } from "../config/flowHubHeroCards.config";
import { checkCompanyAvailability } from "../../../../../../../../../components/signup/helpers/checkCompanyAvailability";
import { useModals } from "../../../../../../../../../context/ModalContext";

// ─── Constants ────────────────────────────────────────────────────────────────

const FLOW_HUB_COMPANY_DRAFT_KEY = "flowHubPendingCompanyDraft";
const FLOW_HUB_COMPANY_RESUME_KEY = "flowHubResumeCompanyDraft";

// Modal keys for BaseModalComponent
const CREATE_COMPANY_MODAL_KEY = "FlowHub-CreateCompany";
const FIND_EXPERT_MODAL_KEY = "FlowHub-FindExpert";

// ─── Draft Helpers ────────────────────────────────────────────────────────────

const saveDraft = (values: TFormValues): void => {
  localStorage.setItem(FLOW_HUB_COMPANY_DRAFT_KEY, JSON.stringify(values));
  localStorage.setItem(FLOW_HUB_COMPANY_RESUME_KEY, "true");
};

const clearDraft = (): void => {
  localStorage.removeItem(FLOW_HUB_COMPANY_DRAFT_KEY);
  localStorage.removeItem(FLOW_HUB_COMPANY_RESUME_KEY);
};

const getStoredCompanyDraft = (): TFormValues => {
  const rawDraft = localStorage.getItem(FLOW_HUB_COMPANY_DRAFT_KEY);
  if (!rawDraft) return {};
  try {
    return JSON.parse(rawDraft) as TFormValues;
  } catch {
    clearDraft();
    return {};
  }
};

// ─── Shared Config ────────────────────────────────────────────────────────────

const baseDialogConfig = {
  styles: {
    paper: { width: "100%", maxWidth: 440 },
    content: { px: 4, pt: 4, pb: 2.5, textAlign: "center" },
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

type PageBuilderAccessPromptType = "guest" | "company";

// ─── Modal content components (defined outside the hook to prevent flickering) ──

interface CreateCompanyContentProps {
  formInstanceKey: number;
  formDefaultValues: TFormValues;
  token: string | null;
  navigate: ReturnType<typeof useNavigate>;
  createCompany: (values: any) => Promise<any>;
  setFormDefaultValues: React.Dispatch<React.SetStateAction<TFormValues>>;
  closeModal: () => void;
}

export const CreateCompanyContent = ({
  formInstanceKey,
  formDefaultValues,
  token,
  navigate,
  createCompany,
  setFormDefaultValues,
  closeModal,
}: CreateCompanyContentProps) => {
  const [isEmailPromptOpen, setIsEmailPromptOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<any>(null);

  const handlePromptNavigation = (path: string) => {
    if (pendingValues) {
      saveDraft(pendingValues);
      setFormDefaultValues(pendingValues);
    }
    setIsEmailPromptOpen(false);
    closeModal();
    navigate(path);
  };

  const handleLoginFromPrompt = () => handlePromptNavigation("/signin");
  const handleCreateAccountFromPrompt = () => handlePromptNavigation("/signup");

  const handleFormSubmit = async (values: any) => {
    if (!token) {
      const email = typeof values?.email === "string" ? values.email.trim() : "";

      if (email) {
        const isAvailable = await checkCompanyAvailability(email);
        if (!isAvailable) {
          setPendingValues(values);
          setIsEmailPromptOpen(true);
          return;
        }
      }

      saveDraft(values);
      setFormDefaultValues(values);
      closeModal();
      navigate("/signup");
      return;
    }

    await createCompany(values);
    clearDraft();
    closeModal();
  };

  return (
    <>
      <Box sx={{ p: 3, backgroundColor: "var(--fx-surface-base, #FFFFFF)", height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight="700" color="var(--fx-surface-text, #0F172A)">
              Create Your Company
            </Typography>
            <Typography variant="body2" color="#7c86c0">
              Fill in the details below to set up your business profile.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 0 }}>
          <BaseForm
            fields={companyFormFields}
            defaultValues={formDefaultValues}
            columns={2}
            submitLabel="Create Company"
            onSubmit={handleFormSubmit}
            styles={flowHubHeroCardsFormStyles}
            onAddressSelect={(addressData, updateField) => {
              updateField("address", addressData.address);
              updateField("city", addressData.city);
              updateField("state", addressData.state);
              updateField("zipCode", addressData.zipCode);
              updateField("country", addressData.country);
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={isEmailPromptOpen}
        onClose={() => setIsEmailPromptOpen(false)}
        styles={{ zIndex: 99999 }}
        config={baseDialogConfig}
        primaryButton={{ label: "Log in", variant: "primary", onClick: handleLoginFromPrompt }}
        secondaryButton={{ label: "Create new account", variant: "secondary", onClick: handleCreateAccountFromPrompt }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="h5" fontWeight={800} color="var(--fx-surface-text, #0F172A)">
            Email already registered
          </Typography>
          <Typography variant="body1" color="var(--fx-surface-textsecondary, #64748B)" sx={{ lineHeight: 1.6 }}>
            This email address is already registered in our system. Would you like to log in to your account to create the company, or create a new
            account?
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

interface FindExpertContentProps {
  token: string | null;
  hasCompany: boolean;
  showGuestPrompt: boolean;
  showCompanyPrompt: boolean;
  handleExploreDemo: () => void;
  handleCreateCompany: () => void;
  navigate: ReturnType<typeof useNavigate>;
}

export const FindExpertContent = ({
  token,
  hasCompany,
  showGuestPrompt,
  showCompanyPrompt,
  handleExploreDemo,
  handleCreateCompany,
  navigate,
}: FindExpertContentProps) => {
  const isShowingGuestPrompt = !token && showGuestPrompt;
  const isShowingCompanyPrompt = token && !hasCompany && showCompanyPrompt;
  const isShowingPrompt = isShowingGuestPrompt || isShowingCompanyPrompt;

  return (
    <Box
      sx={{
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        marginTop: {
          xs: 0,
          md: "-40px",
          xl: "-40px",
        },
        marginLeft: {
          xs: 0,
          md: "-20px",
          xl: "-40px",
        },

        // border: "2px solid #ef4444",
        height: "auto",
        "& main.MuiContainer-root": {
          height: "auto",
          maxHeight: "450px",
          // border: "2px solid #3b82f6",
        },
        "& .MuiContainer-root": {
          height: "auto",
          minHeight: 0,
          margin: 0,
          py: { xs: 2, md: 3, xl: 3 },
        },
        "& .MuiPaper-root": {
          //  border: "2px solid #9eff02",
          height: "auto",
          minHeight: 200,
        },
      }}
    >
      {isShowingPrompt ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            minHeight: "450px",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 2,
              borderRadius: "50%",
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Box sx={{ color: colorsConfig.textPrimary, fontSize: "2.5rem", display: "flex" }}>
              <BusinessCenterIcon sx={{ fontSize: "inherit" }} />
            </Box>
          </Box>

          <Box>
            <Typography variant="h4" fontWeight="700" color="var(--fx-surface-text, #0F172A)" gutterBottom>
              {isShowingGuestPrompt ? "Find Your Expert" : "Create a company first"}
            </Typography>
            <Typography variant="body1" color="#7c86c0" sx={{ maxWidth: "400px", mx: "auto" }}>
              {isShowingGuestPrompt
                ? "Connect with verified professionals. Sign in to unlock full features or explore our demo version."
                : "Your account can access the platform, but finding connections requires a company profile first."}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2, mt: 2 }}>
            {isShowingGuestPrompt ? (
              <>
                <Button
                  variant="contained"
                  onClick={() => navigate("/signup")}
                  sx={{
                    bgcolor: colorsConfig.textPrimary,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#0f1932" },
                  }}
                >
                  Create Account
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/signin")}
                  sx={{
                    borderColor: colorsConfig.textPrimary,
                    color: colorsConfig.textPrimary,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { borderColor: "var(--fx-surface-text, #0F172A)", bgcolor: "rgba(25, 118, 210, 0.04)" },
                  }}
                >
                  Sign In
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateCompany}
                sx={{
                  bgcolor: colorsConfig.textPrimary,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#0f1932" },
                }}
              >
                Create Company
              </Button>
            )}
            <Button
              variant="text"
              onClick={handleExploreDemo}
              sx={{
                color: colorsConfig.textPrimary,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                textDecoration: "underline",
              }}
            >
              Try Demo Version
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 0 }}>
          <ConnectBusinessAlgorithm />
        </Box>
      )}
    </Box>
  );
};

interface PageBuilderAccessPromptModalProps {
  type: PageBuilderAccessPromptType | null;
  onClose: () => void;
  onCreateAccount: () => void;
  onCreateCompany: () => void;
  onLogin: () => void;
}

export const PageBuilderAccessPromptModal = ({ type, onClose, onCreateAccount, onCreateCompany, onLogin }: PageBuilderAccessPromptModalProps) => {
  const [displayType, setDisplayType] = useState<PageBuilderAccessPromptType>("guest");

  useEffect(() => {
    if (type) setDisplayType(type);
  }, [type]);

  const isGuestPrompt = displayType === "guest";

  return (
    <Dialog open={Boolean(type)} onClose={onClose} styles={{ zIndex: 99999 }} config={baseDialogConfig}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="h5" fontWeight={800} color="var(--fx-surface-text, #0F172A)">
          {isGuestPrompt ? "Create an account to continue" : "Create a company first"}
        </Typography>

        <Typography variant="body1" color="var(--fx-surface-textsecondary, #64748B)" sx={{ lineHeight: 1.6 }}>
          {isGuestPrompt
            ? "You need to create an account before you can access the Page Builder editor."
            : "Your account can access the platform, but Page Builder requires a company profile first."}
        </Typography>

        <Button
          variant="contained"
          onClick={isGuestPrompt ? onCreateAccount : onCreateCompany}
          sx={{
            bgcolor: "#f28c28",
            color: "#fff",
            px: 3,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#e67e22", boxShadow: "none" },
          }}
        >
          {isGuestPrompt ? "Create Account" : "Create Company"}
        </Button>

        {isGuestPrompt && (
          <Button
            variant="contained"
            onClick={onLogin}
            sx={{
              bgcolor: "#0f1932",
              color: "#fff",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { bgcolor: "#091224", boxShadow: "none" },
            }}
          >
            Log In
          </Button>
        )}

        <Button
          variant="text"
          onClick={onClose}
          sx={{
            color: "var(--fx-surface-textsecondary, #64748B)",
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Dismiss
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useFlowHubCards = () => {
  const [formDefaultValues, setFormDefaultValues] = useState<TFormValues>(() => getStoredCompanyDraft());
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [showGuestPrompt, setShowGuestPrompt] = useState(true);
  const [showCompanyPrompt, setShowCompanyPrompt] = useState(true);
  const [pageBuilderPromptType, setPageBuilderPromptType] = useState<PageBuilderAccessPromptType | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openModal, closeByKey } = useModals();
  const token = sessionStorage.getItem("token");
  const company = useAppSelector(selectCurrentCompany);
  const roleType = useAppSelector(selectRole);
  const basePath = getBasePath(roleType);
  const { mutateAsync: createCompany } = useCreateCompany();

  const closeCreateCompanyModal = () => closeByKey(CREATE_COMPANY_MODAL_KEY);

  const openCreateCompanyModal = () => {
    const draftValues = getStoredCompanyDraft();
    setFormDefaultValues(draftValues);
    setFormInstanceKey((prev) => prev + 1);

    openModal(
      CREATE_COMPANY_MODAL_KEY,
      <CreateCompanyContent
        formInstanceKey={formInstanceKey + 1}
        formDefaultValues={draftValues}
        token={token}
        navigate={navigate}
        createCompany={createCompany}
        setFormDefaultValues={setFormDefaultValues}
        closeModal={() => closeByKey(CREATE_COMPANY_MODAL_KEY)}
      />,
    );
  };

  const closeFindExpertModal = () => {
    closeByKey(FIND_EXPERT_MODAL_KEY);
    setTimeout(() => {
      setShowGuestPrompt(true);
      setShowCompanyPrompt(true);
      dispatch(resetConnectBusinessState());
    }, 300);
  };

  const handleExploreDemo = () => {
    dispatch(updateConnectBusinessState({ isDemo: true }));
    setShowGuestPrompt(false);
    setShowCompanyPrompt(false);
  };

  const openFindExpertModal = () => {
    openModal(
      FIND_EXPERT_MODAL_KEY,
      <FindExpertContent
        token={token}
        hasCompany={Boolean(company?.id)}
        showGuestPrompt={showGuestPrompt}
        showCompanyPrompt={showCompanyPrompt}
        handleExploreDemo={handleExploreDemo}
        handleCreateCompany={() => {
          closeFindExpertModal();
          openCreateCompanyModal();
        }}
        navigate={navigate}
      />,
    );
  };

  const closePageBuilderPrompt = () => setPageBuilderPromptType(null);

  const handleCreateAccountFromPrompt = () => {
    closePageBuilderPrompt();
    navigate("/signup");
  };

  const handleCreateCompanyFromPrompt = () => {
    closePageBuilderPrompt();
    openCreateCompanyModal();
  };

  const openPageBuilder = () => {
    if (!token) {
      setPageBuilderPromptType("guest");
      return;
    }
    if (!company?.id) {
      setPageBuilderPromptType("company");
      return;
    }
    navigate(`${basePath}/page-builder`);
  };

  // Auto-resume draft on login
  useEffect(() => {
    if (!token || localStorage.getItem(FLOW_HUB_COMPANY_RESUME_KEY) !== "true") return;

    const draftValues = getStoredCompanyDraft();
    setFormDefaultValues(draftValues);
    setFormInstanceKey((prev) => prev + 1);

    openModal(
      CREATE_COMPANY_MODAL_KEY,
      <CreateCompanyContent
        formInstanceKey={formInstanceKey + 1}
        formDefaultValues={draftValues}
        token={token}
        navigate={navigate}
        createCompany={createCompany}
        setFormDefaultValues={setFormDefaultValues}
        closeModal={() => closeByKey(CREATE_COMPANY_MODAL_KEY)}
      />,
    );
  }, [token]);

  return {
    openCreateCompanyModal,
    openFindExpertModal,
    openPageBuilder,
    createCompanyModalKey: CREATE_COMPANY_MODAL_KEY,
    findExpertModalKey: FIND_EXPERT_MODAL_KEY,
    createCompanyModalContentProps: {
      formInstanceKey,
      formDefaultValues,
      token,
      navigate,
      createCompany,
      setFormDefaultValues,
      closeModal: closeCreateCompanyModal,
    },
    findExpertModalContentProps: {
      token,
      hasCompany: Boolean(company?.id),
      showGuestPrompt,
      showCompanyPrompt,
      handleExploreDemo,
      handleCreateCompany: () => {
        closeFindExpertModal();
        openCreateCompanyModal();
      },
      navigate,
    },
    pageBuilderPromptModalProps: {
      type: pageBuilderPromptType,
      onClose: closePageBuilderPrompt,
      onCreateAccount: handleCreateAccountFromPrompt,
      onCreateCompany: handleCreateCompanyFromPrompt,
      onLogin: () => {
        closePageBuilderPrompt();
        navigate("/signin");
      },
    },
  };
};
